import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';

import {
  Envelope,
  Lock
} from '@phosphor-icons/react';
const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Clear any stale tokens on mount (e.g., after JWT secret rotation)
  useEffect(() => {
    // If user lands on login page, clear old tokens to prevent validation issues
    console.log('[AdminLogin] Clearing old admin_token on mount');
    localStorage.removeItem('admin_token');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Clear any existing token before login attempt
    console.log('[AdminLogin] Clearing admin_token before login attempt');
    localStorage.removeItem('admin_token');

    try {
      // Call the dedicated admin login endpoint
      console.log('[AdminLogin] Sending login request...');
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Invalid credentials');
      }
      
      const data = await response.json();
      // Store token in localStorage for backwards compat (httpOnly cookie is set by backend)
      if (data.access_token) {
        localStorage.setItem('admin_token', data.access_token);
      }
      
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#161B26] flex items-center justify-center px-6">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-20"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src={`${process.env.PUBLIC_URL}/man-and-machine-logo.png`}
              alt="Man & Machine" 
              className="h-16 mx-auto mb-4"
            />
          </Link>
          <p className="text-sm text-[#A0AEC0]">Admin Portal</p>
        </div>

        <div className="glass rounded-3xl p-8 card-glow">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Admin Login</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                Email
              </label>
              <div className="relative">
                <Envelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#718096]" size={20} />
                <input
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  className="w-full pl-12 pr-4 py-3 rounded-full glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors placeholder-[#718096]"
                  placeholder="Enter email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#718096]" size={20} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-full glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors placeholder-[#718096]"
                  placeholder="Enter password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full relative overflow-hidden rounded-full group disabled:opacity-50"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] via-[#8B5CF6] to-[#EC4899]"></div>
              <span className="relative z-10 block px-6 py-3 text-white font-semibold">
                {isLoading ? 'Logging in...' : 'Login'}
              </span>
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/admin/forgot-password" 
              className="text-sm text-[#6366F1] hover:text-[#818CF8] transition-colors"
            >
              Forgot your password?
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;